import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  useCreateWwwWebFilter,
  useUpdateWwwWebFilter,
} from '@/api/generated/www-web-filters/www-web-filters';
import type { WebFilterWriteResponse } from '@/api/generated/schemas';
import {
  buildWebFilterFormSchema,
  emptyWebFilterFormValues,
  type WebFilterFormValues,
} from './webFilterFormSchema';
import { toWebFilterRequestBody } from './toWebFilterRequest';
import type { PlacementOrderEditorState } from '../internal/placementOrderModel';

const SERVER_FIELD_MAP: ServerFieldMap = {
  Name: 'name',
  WebProjectId: 'webProjectId',
};

interface UseWebFilterFormOptions {
  webFilterId?: string;
  initialValues?: WebFilterFormValues;
  getPlacementOrder?: () => PlacementOrderEditorState | undefined;
  /** `mode` odlišuje založení od editace — okno se po založení musí překlopit do editace. */
  onSaved: (response: WebFilterWriteResponse, mode: 'created' | 'updated') => void;
  onError?: (error: unknown) => void;
}

export function useWebFilterForm({
  webFilterId,
  initialValues,
  getPlacementOrder,
  onSaved,
  onError,
}: UseWebFilterFormOptions) {
  const { t } = useTranslation('www');
  const schema = useMemo(() => buildWebFilterFormSchema(t), [t]);

  const createMutation = useCreateWwwWebFilter();
  const updateMutation = useUpdateWwwWebFilter();
  const successRef = useRef(false);
  const getPlacementOrderRef = useRef(getPlacementOrder);
  getPlacementOrderRef.current = getPlacementOrder;

  const form = useForm({
    defaultValues: initialValues ?? emptyWebFilterFormValues(0, ''),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      const data = toWebFilterRequestBody(value, getPlacementOrderRef.current?.());
      try {
        if (webFilterId) {
          const response = await updateMutation.mutateAsync({ id: webFilterId, data });
          onSaved(response, 'updated');
        } else {
          const response = await createMutation.mutateAsync({ data });
          onSaved(response, 'created');
        }
        successRef.current = true;
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  return {
    form,
    submit,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
  };
}

export type WebFilterFormApi = ReturnType<typeof useWebFilterForm>['form'];
